import api from './api'

const matchService = {
  async getMatches(filters = {}) {
    const params = new URLSearchParams()
    if (filters.category) params.append('category', filters.category)
    if (filters.status)   params.append('status', filters.status)
    if (filters.skip)     params.append('skip', filters.skip)
    if (filters.limit)    params.append('limit', filters.limit)
    const response = await api.get(`/matches/?${params.toString()}`)
    return response.data
  },

  async getMatch(matchId) {
    const response = await api.get(`/matches/${matchId}`)
    return response.data
  },

  async createMatch(matchData) {
    // FIX : slash final pour éviter le 307 Redirect qui tue le body POST
    const response = await api.post('/matches/', matchData)
    return response.data
  },

  async updateMatch(matchId, matchData) {
    const response = await api.patch(`/matches/${matchId}/`, matchData)
    return response.data
  },

  // PARCOURS-VIDEO-DECLARE-005 · `uploadMatch` A ETE SUPPRIMEE ICI.
  //
  // Elle creait le match ET envoyait la video en un seul appel, pour la page
  // `/dashboard/matches/upload` — retiree dans ce meme lot. `CreateMatch` fait
  // autrement · il cree le match a l'etape 1, puis envoie la video a l'etape 4.
  // Deux etapes, mais un match qui survit a un upload interrompu.
  //
  // Aucun autre appelant. La laisser aurait offert au prochain qui passe un
  // chemin de creation qui ne pose NI la declaration video, NI les couleurs de
  // maillot — les deux garde-fous ajoutes ces deux derniers jours.

  /** Lance la découpe serveur MT1/MT2 (202). */
  async splitHalves(matchId) {
    const response = await api.post(`/matches/${matchId}/split-halves/`)
    return response.data
  },

  /**
   * Etat du processing video lu en base.
   * @param {object} config - config axios (P0-WIZARD-SPLIT-001 : le suivi du
   *   wizard passe un `timeout`, pour qu'une requete restee pendante ne fige
   *   pas le suivi jusqu'a la fin des temps).
   */
  async getVideoStatus(matchId, config = {}) {
    const response = await api.get(`/matches/${matchId}/video-status`, config)
    return response.data
  },

  /**
   * Lance l'analyse IA. Idempotent cote serveur depuis le 02/08/2026 : si la
   * fin du split l'a deja demarree, la route repond un succes decrivant l'etat
   * courant au lieu d'un 409.
   *
   * Pas de slash final · la route backend est `POST /{match_id}/analyze`
   * (app/routes/matches.py). Le slash de trop declenchait un 307, la seule
   * redirection du parcours video. MatchDetail appelait deja sans.
   */
  async launchAnalysis(matchId) {
    const response = await api.post(`/matches/${matchId}/analyze`)
    return response.data
  },

  // P1-SPLIT-MATCHDETAIL-001 · `pollUntilHalvesReady` et
  // `validateTimelineAndAnalyze` ONT ETE SUPPRIMEES ICI, sans remplacant.
  //
  // C'etait une boucle `while` bloquante, sans timeout par requete : une seule
  // requete restee pendante suspendait le suivi jusqu'a la fin des temps. Elle
  // ne pouvait pas non plus survivre a un rechargement, ni etre relancee au
  // retour d'un onglet endormi, puisqu'elle vivait dans la closure de son
  // appelant. C'est le defaut qui a fige l'ecran du 09/08 alors que les deux
  // mi-temps etaient deja ecrites en base.
  //
  // Le suivi appartient desormais aux ecrans, en `useEffect` ancre au montage,
  // qui lisent `getVideoStatus` avec un timeout et s'arretent sur `has_halves`
  // (CreateMatch.jsx pour le wizard, MatchDetail.jsx pour la page du match).
  // La laisser en place, meme sans appelant, aurait remis le piege a
  // disposition du prochain qui passe.

  async getVideoUrl(matchId, half) {
    const params = half ? `?half=${half}` : ''
    const response = await api.get(`/matches/${matchId}/video-url${params}`)
    return response.data
  },

  /** Statut agrégé analyse IA (worker backend → API externe). */
  async getAnalysisStatus(matchId) {
    const response = await api.get(`/matches/${matchId}/analysis-status`)
    return response.data
  },

  /**
   * Résultat AnalyseResult stocké en S3.
   * @param {'1'|'2'|'full'|1|2} half
   */
  async getAnalysisResult(matchId, half = 'full') {
    const response = await api.get(`/matches/${matchId}/analysis-result`, {
      params: { half },
    })
    return response.data
  },

  /**
   * Charge mt1 + mt2 (+ full si fourni par le backend).
   * Tolère 404 tant que le backend n'est pas déployé.
   */
  async loadAnalysisResults(matchId) {
    const out = { mt1: null, mt2: null, full: null, error: null }
    try {
      const [r1, r2, rFull] = await Promise.allSettled([
        this.getAnalysisResult(matchId, 1),
        this.getAnalysisResult(matchId, 2),
        this.getAnalysisResult(matchId, 'full'),
      ])
      if (r1.status === 'fulfilled') out.mt1 = r1.value?.result ?? r1.value
      if (r2.status === 'fulfilled') out.mt2 = r2.value?.result ?? r2.value
      if (rFull.status === 'fulfilled') out.full = rFull.value?.result ?? rFull.value
      if (!out.mt1 && !out.mt2 && !out.full) {
        const err = [r1, r2, rFull].find((r) => r.status === 'rejected')
        if (err?.reason?.response?.status === 404) {
          out.error = null // endpoint pas encore dispo
        } else if (err?.reason) {
          out.error = err.reason?.response?.data?.detail || err.reason?.message || 'Erreur chargement analyse'
        }
      }
    } catch (e) {
      out.error = e?.message || 'Erreur chargement analyse'
    }
    return out
  },

  async deleteMatch(matchId) {
    await api.delete(`/matches/${matchId}`)
  },
}

export default matchService
